import { useState, useMemo } from 'react';
import {
    DateTimePicker,
    DateTimePickerProps,
    DateTimeValidationError,
} from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { Controller, FieldValues } from 'react-hook-form';
import { DateTime } from 'luxon';

import { getDateTimeFormat } from 'common/utils/getDateFormat';
import { valueToDateTime } from 'common/utils/formatDate';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps extends Omit<DateTimePickerProps, 'name' | 'onChange'> {
    name: string;
    onChange?: (value: DateTime) => void;
}

export const DateTimePickerField = <TFieldValues extends FieldValues>({
    control: formControl,
    label,
    name,
    onChange,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const dateFormat = getDateTimeFormat();
    const [translate] = useTranslation();

    const control = asTypedControl<string>(name, formControl);

    const [error, setError] = useState<DateTimeValidationError | null>(null);

    const errorMessage = useMemo(() => {
        switch (error) {
            case 'maxDate':
                return translate('maxDateMessage');

            case 'minDate':
                return translate('minDateMessage');

            case 'invalidDate': {
                return translate('invalidDateMessage');
            }

            default:
                return '';
        }
    }, [error]);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error: fieldError } }) => (
                <DateTimePicker
                    {...field}
                    {...other}
                    ampm={false}
                    format={dateFormat}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            margin: 'normal',
                            label: label,
                            error: !!fieldError || !!error,
                            helperText: fieldError?.message || errorMessage,
                            sx: {
                                '& .MuiPickersInputBase-sectionsContainer': {
                                    py: 2,
                                },
                            },
                        },
                    }}
                    // Make sure we have a luxon DateTime object
                    value={valueToDateTime(field.value) as DateTime}
                    onChange={(newDate) => {
                        if (!!onChange) {
                            onChange?.(newDate as DateTime);

                            return;
                        }

                        field.onChange(newDate);
                    }}
                    onError={(newError) => setError(newError)}
                />
            )}
        />
    );
};
